import { useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useTargetFacilities, Facility } from "../../../lib/api";

/**
 * TargetFacilitySelector Component
 *
 * Fetches facilities from SERVER_CONFIG endpoint (no need to select instance/client)
 */
interface TargetFacilitySelectorProps {
  className?: string;
  label?: string;
}

export function TargetFacilitySelector({
  className,
  label = "Target Facility"
}: TargetFacilitySelectorProps) {
  const {
    targetFacilityId,
    setTargetFacilityId,
    setTargetUseCaseId,
    setTargetAuthorId
  } = useWorkflowStore();

  // Fetch facilities from SERVER_CONFIG endpoint
  const {
    data: facilities = [],
    isLoading,
    error
  } = useTargetFacilities();

  // Reset dependent selections when facility changes
  useEffect(() => {
    setTargetUseCaseId(null);
    setTargetAuthorId(null);
  }, [targetFacilityId, setTargetUseCaseId, setTargetAuthorId]);

  // Handle facility selection
  const handleFacilityChange = (value: string) => {
    setTargetFacilityId(value);
  };

  return (
    <div className={className}>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor="target-facility">{label}</Label>
        <Select
          value={targetFacilityId || ""}
          onValueChange={handleFacilityChange}
          disabled={isLoading}
        >
          <SelectTrigger id="target-facility">
            <SelectValue placeholder="Select target facility" />
          </SelectTrigger>
          <SelectContent>
            {isLoading ? (
              <SelectItem value="loading" disabled>
                Loading facilities...
              </SelectItem>
            ) : error ? (
              <SelectItem value="error" disabled>
                Error: {error instanceof Error ? error.message : "Failed to load facilities"}
              </SelectItem>
            ) : facilities.length === 0 ? (
              <SelectItem value="none" disabled>
                No facilities available
              </SelectItem>
            ) : (
              facilities.map((facility: Facility) => (
                <SelectItem key={facility.id} value={facility.id}>
                  {facility.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}